/**
 * useBookingFiles — Fetch reservation-linked files and hasFiles for BookingDetailModal
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 5b-2c: Extracted from BookingDetailModal.jsx files state and effects.
 *
 * Purpose:
 *   (1) Lightweight hasFiles check when modal opens or reservation changes (Files icon blue dot).
 *   (2) Fetch reservation-scoped files list when Files panel opens (topPanelOpen === 'files').
 *   Same endpoints, params, gating, and loading/error/empty behavior as the original inline effects.
 *
 * Workflow:
 *   1. Effect A (hasFiles): when isOpen && reservationId, call getReservationHasFilesFn; or set
 *      hasFiles true when files already loaded for same reservation. When !isOpen, reset state.
 *   2. Effect B (files list): when topPanelOpen === 'files' && reservationId, call
 *      getReservationLinksFn({ reservation_id, include_property_links: false }); 8s timeout;
 *      404/403 → empty; other errors → error with retry. Skip fetch if already loaded and no error
 *      and filesRetryCount === 0.
 *   3. refreshFiles: clear error, clear files, increment retry count (wired to FilesPanel onRetry).
 *
 * Assumptions & Invariants:
 *   - getReservationHasFilesFn(rid) and getReservationLinksFn({ reservation_id, include_property_links })
 *     match ownerApi.getReservationHasFiles / getReservationLinks.
 *   - Effect deps preserved: hasFiles effect [isOpen, reservationId, files]; list effect
 *     [topPanelOpen, reservationId, filesRetryCount].
 *   - No new endpoints; no change to call count or timing.
 *
 * Dependencies:
 *   - React (useState, useEffect)
 *   - getReservationHasFilesFn, getReservationLinksFn passed in by caller
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 5b-2c: Extracted from BookingDetailModal.jsx; AbortController + isActive added.
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * @param {Object} opts
 * @param {boolean} opts.isOpen - Modal open
 * @param {string|number|null|undefined} opts.reservationId - Reservation ID
 * @param {string|number|null|undefined} opts.listingId - Hostaway listing ID (required for full links list; from booking.listing_id)
 * @param {string|null} opts.topPanelOpen - 'reviews' | 'notes' | 'files' | null
 * @param {function(string|number): Promise<Object>} opts.getReservationHasFilesFn - e.g. getReservationHasFiles
 * @param {function(Object): Promise<Object>} opts.getReservationLinksFn - e.g. getReservationLinks
 * @returns {{ filesLoading: boolean, filesError: string|null, files: Array, hasFiles: boolean, refreshFiles: function }}
 */
export function useBookingFiles({
  isOpen,
  reservationId,
  listingId,
  topPanelOpen,
  getReservationHasFilesFn,
  getReservationLinksFn,
}) {
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState(null);
  const [filesRetryCount, setFilesRetryCount] = useState(0);
  const [hasFiles, setHasFiles] = useState(false);

  // Effect A: hasFiles check when modal opens or reservation changes (blue dot)
  // Contract: has_files_only request must include listing_id; skip API when listingId missing.
  // Guard: only set state when value actually changes to avoid "Maximum update depth exceeded"
  // (e.g. setFiles([]) would create a new reference every run and re-trigger this effect).
  useEffect(() => {
    if (!isOpen) {
      if (hasFiles) setHasFiles(false);
      if (files.length > 0) setFiles([]);
      if (filesError != null) setFilesError(null);
      if (filesRetryCount !== 0) setFilesRetryCount(0);
      return;
    }
    if (!reservationId) {
      if (hasFiles) setHasFiles(false);
      return;
    }
    if (!listingId) {
      if (hasFiles) setHasFiles(false);
      return;
    }
    if (files.length > 0) {
      if (!hasFiles) setHasFiles(true);
      return;
    }
    const abortController = new AbortController();
    let isActive = true;
    getReservationHasFilesFn(reservationId, listingId)
      .then((res) => {
        if (!isActive) return;
        const val = res?.data?.hasFiles === true;
        setHasFiles(!!val);
      })
      .catch(() => {
        if (isActive) setHasFiles(false);
      });
    return () => {
      abortController.abort();
      isActive = false;
    };
  }, [isOpen, reservationId, listingId, files, hasFiles, filesError, filesRetryCount]);

  // Effect B: files list when Files panel opens (backend requires listing_id for full list)
  // Guard setState to avoid update loops when reservationId/listingId missing.
  useEffect(() => {
    if (topPanelOpen !== 'files') {
      if (filesError) setFilesError(null);
      if (filesRetryCount > 0) setFilesRetryCount(0);
      return;
    }
    if (!reservationId) {
      if (files.length > 0) setFiles([]);
      if (filesLoading) setFilesLoading(false);
      if (filesError != null) setFilesError(null);
      return;
    }
    if (!listingId) {
      if (files.length > 0) setFiles([]);
      if (filesLoading) setFilesLoading(false);
      setFilesError('Property (listing) is unknown for this reservation; files cannot be loaded.');
      return;
    }
    if (files.length > 0 && !filesError && filesRetryCount === 0) {
      return;
    }

    const abortController = new AbortController();
    let isActive = true;
    let timeoutId = null;

    setFilesLoading(true);
    setFilesError(null);

    timeoutId = setTimeout(() => {
      if (isActive) {
        console.warn('[BookingDetailModal] Files fetch timeout after 8s');
        setFilesLoading(false);
        setFilesError('Could not load files (timeout)');
        setFiles([]);
      }
    }, 8000);

    getReservationLinksFn({ reservation_id: reservationId, listing_id: listingId, include_property_links: false })
      .then((response) => {
        clearTimeout(timeoutId);
        if (!isActive) return;

        const rawLinks = response?.data?.links ?? response?.links ?? (Array.isArray(response) ? response : []);
        const linksArray = Array.isArray(rawLinks) ? rawLinks : [];

        if (response?.outcome === 'success' && response?.data?.hasFiles !== undefined && response?.data?.links === undefined) {
          setFiles([]);
          setFilesLoading(false);
          setFilesError(null);
          return;
        }

        const reservationFiles = linksArray.filter((link) => link && link.scope === 'reservation');
        reservationFiles.sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
          const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
          return dateB - dateA;
        });
        setFiles(reservationFiles);
        setFilesLoading(false);
        setFilesError(null);
        if (reservationFiles.length > 0) setHasFiles(true);
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (!isActive) return;
        if (err?.name === 'AbortError') return;

        console.error('[BookingDetailModal] Error fetching files:', err);
        if (err?.status === 404 || err?.status === 403) {
          setFiles([]);
          setFilesLoading(false);
          setFilesError(null);
        } else {
          setFilesError(err?.message || 'Failed to load files. Please try again.');
          setFilesLoading(false);
        }
      });

    return () => {
      abortController.abort();
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [topPanelOpen, reservationId, listingId, filesRetryCount]);

  const refreshFiles = useCallback(() => {
    setFilesError(null);
    setFiles([]);
    setFilesRetryCount((prev) => prev + 1);
  }, []);

  return { filesLoading, filesError, files, hasFiles, refreshFiles };
}
